import React, {useContext, useEffect, useState} from 'react';
import {TitleContext} from 'components/TitleContext';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import Table from 'components/table/Table';
import Skeleton from 'react-loading-skeleton';
import {UserHrDocumentThunks} from 'features/userHrDocument/userHrDocumentThunks';
import {UserTrainingThunks} from 'features/userTraining/userTrainingThunks';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import useViewerModal from 'hooks/useViewerModal';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import DotsMenu from 'components/DotsMenu';
import {SearchType} from 'types/search';
import {UserHrDocument} from 'types/userHrDocument';
import {UserTraining} from 'types/userTraining';
import XCircleIcon from 'assets/images/icons/XCircleIcon';
import {setMyDashboardTab as setActiveTab} from 'features/settings/settingsSlice';
import Drawer from 'components/Drawer';
import UserHrDocumentForm from 'features/userHrDocument/UserHrDocumentForm';
import UserTrainingForm from 'features/userTraining/UserTrainingForm';
import {useNavigate} from 'react-router-dom';
import {UserApplicationThunks} from 'features/userApplication/userApplicationThunks';
import {UserApplication} from '../../types/userApplication';
import UserApplicationForm from '../../features/userApplication/UserApplicationForm';
import {UserEvaluationThunks} from 'features/userEvaluation/userEvaluationThunks';
import {UserEvaluation} from 'types/userEvaluation';
import UserEvaluationForm from 'features/userEvaluation/UserEvaluationForm';
import {UserJobRoleThunks} from 'features/userJobRole/userJobRoleThunks';
import {UserJobRole} from 'types/userJobRole';

function getTabType(
  activeTab?: string,
):
  | 'documents'
  | 'trainings'
  | 'applications'
  | 'evaluations'
  | 'roles'
  | 'other' {
  if (!activeTab) return 'other';
  if (activeTab.includes('Documents')) return 'documents';
  if (activeTab.includes('Trainings')) return 'trainings';
  if (activeTab.includes('Applications')) return 'applications';
  if (activeTab.includes('Evaluations')) return 'evaluations';
  if (activeTab.includes('Roles')) return 'roles';
  return 'other';
}

const MyDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const activeTab = useSelector(
    (state: RootState) => state.settings.myDashboardTab,
  );
  const user = useSelector((state: RootState) => state.auth.user);
  const userHrDocuments = useSelector(
    (state: RootState) => state.userHrDocument.userHrDocuments,
  );
  const trainings = useSelector(
    (state: RootState) => state.userTraining.userTrainings,
  );
  const userApplications = useSelector(
    (state: RootState) => state.userApplication.userApplications,
  );
  const userEvaluations = useSelector(
    (state: RootState) => state.userEvaluation.userEvaluations,
  );
  const userJobRoles = useSelector(
    (state: RootState) => state.userJobRole.userJobRoles,
  );
  const hrDocumentsPagy = useSelector(
    (state: RootState) => state.userHrDocument.pagy,
  );
  const trainingsPagy = useSelector(
    (state: RootState) => state.userTraining.pagy,
  );
  const applicationsPagy = useSelector(
    (state: RootState) => state.userApplication.pagy,
  );
  const evaluationsPagy = useSelector(
    (state: RootState) => state.userEvaluation.pagy,
  );
  const jobRolesPagy = useSelector(
    (state: RootState) => state.userJobRole.pagy,
  );

  const tabType = getTabType(activeTab);

  let defaultPagy;
  switch (tabType) {
    case 'documents':
      defaultPagy = hrDocumentsPagy;
      break;
    case 'trainings':
      defaultPagy = trainingsPagy;
      break;
    case 'applications':
      defaultPagy = applicationsPagy;
      break;
    case 'evaluations':
      defaultPagy = evaluationsPagy;
      break;
    case 'roles':
      defaultPagy = jobRolesPagy;
      break;
    default:
      break;
  }
  const [pagy, setPagy] = useState<any>(defaultPagy);

  const loading = useSelector((state: RootState) => {
    switch (tabType) {
      case 'documents':
        return state.userHrDocument.status === 'loading';
      case 'trainings':
        return state.userTraining.status === 'loading';
      case 'applications':
        return state.userApplication.status === 'loading';
      case 'evaluations':
        return state.userEvaluation.status === 'loading';
      case 'roles':
        return state.userJobRole.status === 'loading';
      default:
        return false;
    }
  });

  let initialRecordName = '';
  switch (tabType) {
    case 'documents':
      initialRecordName = 'Document';
      break;
    case 'trainings':
      initialRecordName = 'Training';
      break;
    case 'applications':
      initialRecordName = 'Application';
      break;
    case 'evaluations':
      initialRecordName = 'Evaluation';
      break;
    case 'roles':
      initialRecordName = 'Role';
      break;
    default:
      break;
  }
  const [recordName, setRecordName] = useState<string>(initialRecordName);

  const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);
  const [isTrainingDrawerOpen, setIsTrainingDrawerOpen] = useState(false);
  const [isApplicationDrawerOpen, setIsApplicationDrawerOpen] = useState(false);
  const [isEvaluationDrawerOpen, setIsEvaluationDrawerOpen] = useState(false);

  const {setTitle} = useContext(TitleContext);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const [records, setRecords] = useState<any[]>([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  interface VisibleColumnsType {
    name: boolean;
    status: boolean;
    notes: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumnsType>({
    name: true,
    status: true,
    notes:
      tabType !== 'evaluations' &&
      tabType !== 'applications' &&
      tabType !== 'roles',
  });
  const [columnOptions, setColumnOptions] = useState<any[]>([
    {label: recordName, value: 'name', isVisible: visibleColumns.name},
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {label: 'Notes', value: 'notes', isVisible: visibleColumns.notes},
  ]);

  const openDrawer = () => {
    switch (tabType) {
      case 'documents':
        setIsDocumentDrawerOpen(true);
        setIsTrainingDrawerOpen(false);
        setIsApplicationDrawerOpen(false);
        setIsEvaluationDrawerOpen(false);
        break;
      case 'trainings':
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(true);
        setIsApplicationDrawerOpen(false);
        setIsEvaluationDrawerOpen(false);
        break;
      case 'applications':
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(false);
        setIsApplicationDrawerOpen(true);
        setIsEvaluationDrawerOpen(false);
        break;
      case 'evaluations':
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(false);
        setIsApplicationDrawerOpen(false);
        setIsEvaluationDrawerOpen(true);
        break;
      default:
        break;
    }
  };

  const {AttachmentModal, handleNameClick} = useViewerModal<any>({
    openDrawer,
    fetchItemThunk: activeTab?.includes('Documents')
      ? UserHrDocumentThunks.show
      : activeTab?.includes('Trainings')
        ? UserTrainingThunks.show
        : activeTab?.includes('Applications')
          ? UserApplicationThunks.show
          : activeTab?.includes('Evaluations')
            ? UserEvaluationThunks.show
            : UserJobRoleThunks.show,
  });

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    if (activeTab?.includes('Missing')) {
      search.where.status = {operator: '==', value: 'missing'};
    }
    if (activeTab?.includes('Expired')) {
      search.where.status = {operator: '==', value: 'expired'};
    }

    search.where.user_id = {
      operator: '==',
      value: user?.id as string,
    };

    switch (tabType) {
      case 'documents':
        await dispatch(
          UserHrDocumentThunks.index({
            page,
            search,
            sortField,
            sortDirection,
            rowsPerPage,
          }),
        );
        break;
      case 'trainings':
        await dispatch(
          UserTrainingThunks.index({
            page,
            search,
            sortField,
            sortDirection,
            rowsPerPage,
          }),
        );
        break;
      case 'applications':
        await dispatch(
          UserApplicationThunks.index({
            page,
            search,
            sortField,
            sortDirection,
            rowsPerPage,
          }),
        );
        break;
      case 'evaluations':
        await dispatch(
          UserEvaluationThunks.index({
            page,
            search,
            sortField,
            sortDirection,
            rowsPerPage,
          }),
        );
        break;
      case 'roles':
        await dispatch(
          UserJobRoleThunks.index({
            page,
            search,
            sortField,
            sortDirection,
            rowsPerPage,
          }),
        );
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (
      !isDocumentDrawerOpen &&
      !isTrainingDrawerOpen &&
      !isApplicationDrawerOpen &&
      !isEvaluationDrawerOpen
    ) {
      setRefreshCounter(prev => prev + 1);
    }
  }, [
    isDocumentDrawerOpen,
    isTrainingDrawerOpen,
    isApplicationDrawerOpen,
    isEvaluationDrawerOpen,
  ]);

  useEffect(() => {
    switch (tabType) {
      case 'documents':
        setRecords(userHrDocuments);
        setPagy(hrDocumentsPagy);
        setRecordName('Document');
        setColumnOptions([
          {label: 'Document', value: 'name', isVisible: visibleColumns.name},
          {label: 'Status', value: 'status', isVisible: visibleColumns.status},
          {label: 'Notes', value: 'notes', isVisible: visibleColumns.notes},
        ]);
        break;
      case 'trainings':
        setRecords(trainings);
        setPagy(trainingsPagy);
        setRecordName('Training');
        setColumnOptions([
          {label: 'Training', value: 'name', isVisible: visibleColumns.name},
          {label: 'Status', value: 'status', isVisible: visibleColumns.status},
          {label: 'Notes', value: 'notes', isVisible: visibleColumns.notes},
        ]);
        break;
      case 'applications':
        setRecords(userApplications);
        setPagy(applicationsPagy);
        setRecordName('Application');
        setColumnOptions([
          {label: 'Application', value: 'name', isVisible: visibleColumns.name},
          {label: 'Status', value: 'status', isVisible: visibleColumns.status},
        ]);
        break;
      case 'evaluations':
        setRecords(userEvaluations);
        setPagy(evaluationsPagy);
        setRecordName('Evaluation');
        setColumnOptions([
          {label: 'Evaluation', value: 'name', isVisible: visibleColumns.name},
          {label: 'Status', value: 'status', isVisible: visibleColumns.status},
        ]);
        break;
      case 'roles':
        setRecords(userJobRoles);
        setPagy(jobRolesPagy);
        setRecordName('Role');
        setColumnOptions([
          {label: 'Role', value: 'name', isVisible: visibleColumns.name},
          {label: 'Status', value: 'status', isVisible: visibleColumns.status},
        ]);
        break;
      default:
        break;
    }
  }, [
    userHrDocuments,
    trainings,
    userApplications,
    userEvaluations,
    userJobRoles,
    activeTab,
    hrDocumentsPagy,
    trainingsPagy,
    applicationsPagy,
    evaluationsPagy,
    jobRolesPagy,
    visibleColumns,
    tabType,
  ]);

  useEffect(() => {
    setTitle(
      <div>
        <div className="text-slate-600 text-lg font-medium">My Dashboard</div>
        <div className="text-slate-400 text-sm font-light">
          {user?.firstName} {user?.lastName}
        </div>
      </div>,
    );
  }, [user, setTitle]);

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleEdit = async (
    record:
      | UserHrDocument
      | UserTraining
      | UserApplication
      | UserEvaluation
      | UserJobRole,
  ) => {
    switch (tabType) {
      case 'documents': {
        const doc = record as UserHrDocument;
        await dispatch(UserHrDocumentThunks.show(doc.id));
        if (doc.hrDocument?.documentType === 'file') {
          setIsDocumentDrawerOpen(true);
          setIsTrainingDrawerOpen(false);
          setIsApplicationDrawerOpen(false);
          setIsEvaluationDrawerOpen(false);
        } else {
          navigate(`/document-fill/${doc.id}`);
        }
        break;
      }
      case 'trainings': {
        await dispatch(UserTrainingThunks.show(record.id));
        setIsTrainingDrawerOpen(true);
        setIsDocumentDrawerOpen(false);
        setIsApplicationDrawerOpen(false);
        setIsEvaluationDrawerOpen(false);
        break;
      }
      case 'applications': {
        const application = record as UserApplication;
        navigate(`/document-fill/${application.documentTemplateSolution.id}`);
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(false);
        setIsEvaluationDrawerOpen(false);
        break;
      }
      case 'evaluations': {
        const evaluation = record as UserEvaluation;
        navigate(`/document-fill/${evaluation.documentTemplateSolution?.id}`);
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(false);
        setIsApplicationDrawerOpen(false);
        break;
      }
      case 'roles': {
        const jobRole = record as UserJobRole;
        if (jobRole.documentTemplateSolution?.id) {
          navigate(`/document-fill/${jobRole.documentTemplateSolution.id}`);
        }
        setIsDocumentDrawerOpen(false);
        setIsTrainingDrawerOpen(false);
        setIsApplicationDrawerOpen(false);
        setIsEvaluationDrawerOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const handleDelete = async (id: string) => {
    switch (tabType) {
      case 'documents': {
        const newHrDocument = {id, attachment: null, deleteAttachment: true};
        await dispatch(UserHrDocumentThunks.update(newHrDocument));
        break;
      }
      case 'trainings': {
        const newTrainings = new UserTraining({
          id,
          attachment: null,
          deleteAttachment: true,
        });
        await dispatch(UserTrainingThunks.update(newTrainings));
        break;
      }
      case 'applications': {
        const newApplication = new UserApplication({
          id,
          attachment: null,
        });
        await dispatch(UserApplicationThunks.update(newApplication));
        break;
      }
      default:
        break;
    }
    setDotsMenuIsOpen(null);
  };

  const handleDownload = (attachmentUrl: string) => {
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  };

  return (
    <>
      <Table
        tabs={[
          'Missing Documents',
          'Expired Documents',
          'Missing Trainings',
          'Expired Trainings',
          'Missing Applications',
          'Missing Evaluations',
          'Missing Roles',
        ]}
        refreshData={refreshCounter}
        activeTab={activeTab}
        fetchData={fetchData}
        setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
        pagy={pagy}
        recordsName={`${recordName}s`}
        loading={loading}
        columns={true}
        optionsByTab={{
          'Missing Documents': {
            searchByOptions: [{label: 'Document', value: 'name'}],
          },
          'Expired Documents': {
            searchByOptions: [{label: 'Document', value: 'name'}],
          },
          'Missing Trainings': {
            searchByOptions: [{label: 'Training', value: 'name'}],
          },
          'Expired Trainings': {
            searchByOptions: [{label: 'Training', value: 'name'}],
          },
          'Missing Applications': {
            searchByOptions: [{label: 'Application', value: 'name'}],
          },
          'Missing Evaluations': {
            searchByOptions: [{label: 'Evaluation', value: 'name'}],
          },
          'Missing Roles': {
            searchByOptions: [{label: 'Role', value: 'name'}],
          },
        }}
        columnOptions={columnOptions}
        searchByOptions={[
          {
            label: recordName,
            operator: 'like',
          },
        ]}
        onColumnToggle={handleColumnToggle}>
        <thead>
          <tr>
            {visibleColumns.name && (
              <TableHeader label={recordName} sortBy="name" />
            )}
            {visibleColumns.status && (
              <TableHeader label="Status" sortBy="status" />
            )}
            {visibleColumns.notes &&
              tabType !== 'evaluations' &&
              tabType !== 'applications' &&
              tabType !== 'roles' && (
                <TableHeader label="Notes" sortBy="notes" />
              )}
            <TableHeader label="" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {records?.length !== 0 &&
            records?.map(
              (
                record:
                  | UserHrDocument
                  | UserTraining
                  | UserApplication
                  | UserEvaluation
                  | UserJobRole,
              ) => {
                const userOptions = [
                  ...(record.status === 'Missing' &&
                  tabType !== 'applications' &&
                  tabType !== 'evaluations' &&
                  tabType !== 'roles'
                    ? [
                        {
                          label: 'Edit',
                          onClick: () => handleEdit(record),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                      ]
                    : []),
                  ...(record.status !== 'Missing'
                    ? [
                        {
                          label: 'Download',
                          onClick: () =>
                            handleDownload(record.attachment?.url || ''),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                        {
                          label: 'Remove attached document',
                          onClick: () => handleDelete(record.id),
                          hoverClass: 'hover:bg-red-100 hover:text-red-800',
                        },
                      ]
                    : []),
                  ...(record.status === 'Missing' &&
                  (!('hrDocument' in record) ||
                    record.hrDocument?.documentType === 'file') &&
                  tabType !== 'applications' &&
                  tabType !== 'evaluations' &&
                  tabType !== 'roles'
                    ? [
                        {
                          label: 'Upload',
                          onClick: () => handleEdit(record),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                      ]
                    : []),
                  ...(record.status === 'Missing' && tabType === 'applications'
                    ? [
                        {
                          label: 'Sign',
                          onClick: () => handleEdit(record),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                      ]
                    : []),
                  ...(record.status === 'Missing' && tabType === 'evaluations'
                    ? [
                        {
                          label: 'Sign',
                          onClick: () => handleEdit(record),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                      ]
                    : []),
                  ...(record.status === 'Missing' && tabType === 'roles'
                    ? [
                        {
                          label: 'Sign',
                          onClick: () => handleEdit(record),
                          hoverClass: 'hover:bg-cyan-100',
                        },
                      ]
                    : []),
                ];

                if (loading) {
                  return (
                    <TableRow key={record.id}>
                      <td colSpan={6}>
                        <Skeleton count={1} height={40} />
                      </td>
                    </TableRow>
                  );
                } else {
                  return (
                    <TableRow key={record.id}>
                      {visibleColumns.name && (
                        <TableCell>
                          <span
                            className={`text-sm font-medium ${
                              record.status !== 'Missing'
                                ? 'underline text-cyan-600 cursor-pointer'
                                : ''
                            }`}
                            onClick={
                              record.status !== 'Missing'
                                ? () => handleNameClick(record)
                                : undefined
                            }>
                            {record.name}
                          </span>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <span
                            className={`flex items-center text-xs font-medium px-2 py-0.5 rounded-full w-32
                            ${record.status === 'Missing' ? 'bg-red-100 text-red-800' : ''}
                            ${record.status === 'Expired' ? 'bg-red-100 text-red-800' : ''}
                          `}>
                            {(record.status === 'Missing' ||
                              record.status === 'Expired') && (
                              <XCircleIcon className="mr-2" />
                            )}
                            <div className="pl-1">{record.status}</div>
                          </span>
                        </TableCell>
                      )}
                      {visibleColumns.notes &&
                        tabType !== 'evaluations' &&
                        tabType !== 'applications' &&
                        tabType !== 'roles' && (
                          <TableCell>
                            {'notes' in record ? record.notes : ''}
                          </TableCell>
                        )}
                      <TableCell>
                        <DotsMenu options={userOptions} />
                      </TableCell>
                    </TableRow>
                  );
                }
              },
            )}
        </tbody>
      </Table>

      <AttachmentModal />

      <Drawer
        isOpen={isDocumentDrawerOpen}
        onClose={() => setIsDocumentDrawerOpen(false)}>
        <UserHrDocumentForm
          closeDrawer={() => setIsDocumentDrawerOpen(false)}
          isDrawerOpen={isDocumentDrawerOpen}
        />
      </Drawer>

      <Drawer
        isOpen={isTrainingDrawerOpen}
        onClose={() => setIsTrainingDrawerOpen(false)}>
        <UserTrainingForm
          closeDrawer={() => setIsTrainingDrawerOpen(false)}
          isDrawerOpen={isTrainingDrawerOpen}
        />
      </Drawer>

      <Drawer
        isOpen={isApplicationDrawerOpen}
        onClose={() => setIsApplicationDrawerOpen(false)}>
        <UserApplicationForm
          closeDrawer={() => setIsApplicationDrawerOpen(false)}
          isDrawerOpen={isApplicationDrawerOpen}
          userId={user?.id || ''}
        />
      </Drawer>

      <Drawer
        isOpen={isEvaluationDrawerOpen}
        onClose={() => setIsEvaluationDrawerOpen(false)}>
        <UserEvaluationForm
          closeDrawer={() => setIsEvaluationDrawerOpen(false)}
          isDrawerOpen={isEvaluationDrawerOpen}
          userId={user?.id || ''}
        />
      </Drawer>
    </>
  );
};

export default MyDashboardPage;
